<template>
    <div class="club event-overview">
        <div class="content">
            <div v-if="!venue" class="loader cards-container"></div>
            <div v-if="venue" class="cards-container">
                <Card class="club">
                    <div class="club-info">
                        <div class="text-container">
                            <div class="label">{{ $t('venue.club_info.headline') }}</div>
                            <h1>{{ venue.name }}</h1>
                        </div>
                        <Button
                            type="dark-secondary"
                            icon="edit"
                            :value="$t('venue.club_info.edit_club')"
                            round
                            @click="$router.push({ name: 'club-settings', params: { id: $route.params.id } })"
                        />
                    </div>
                    <div class="club-image" :style="`background-image: url(${venue.logoUrl ? venue.logoUrl : ''}`" />
                </Card>
                <CapacityCard
                    :current="venueCapacityUsage"
                    :venue-i-d="venue.id"
                    :show-venue="false"
                    :show-add-user="false"
                />
                <Card v-if="false" class="stats">
                    <div class="stat">
                        <div class="number">{{ venue.upcomingEventsCount }}</div>
                        <div class="description">
                            <div class="label">
                                Anstehende <br />
                                veranstaltungen
                            </div>
                        </div>
                    </div>
                    <div class="stat">
                        <div class="number">{{ kFormatter(venue.sourceFanCount) }}</div>
                        <div class="description">
                            <div class="label">
                                Facebook <br />
                                Fans
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
            <div class="tabs-container">
                <router-link class="link fill" :to="{ name: 'venue_overview' }">
                    <Icon name="party-confetti" />
                    <span>{{ $t('venue.tabs.venue_overview') }}</span>
                </router-link>
                <router-link class="link stroke" :to="{ name: 'venue_statistics' }">
                    <Icon name="statistics" />
                    <span>{{ $t('venue.tabs.venue_statistics') }}</span>
                </router-link>
            </div>
            <router-view />
        </div>
    </div>
</template>

<script>
import Button from '@/components/Button'
import Card from '@/components/Card'
import gql from 'graphql-tag'
import CapacityCard from '@/components/CapacityCard'
import Icon from '@/components/Icon'

export default {
    name: 'Venue',
    components: {
        Button,
        Icon,
        Card,
        CapacityCard,
    },
    apollo: {
        venue: {
            query: gql`
                query ($id: ID!) {
                    venue(id: $id) {
                        id
                        email
                        phone
                        websiteUrl
                        dresscode
                        name
                        logoUrl
                        genres
                        priceLevel
                        capacity
                        activeEvents {
                            id
                            capacityUsage
                            capacityInUse
                        }
                    }
                }
            `,
            pollInterval: 5000,
            variables() {
                return {
                    id: this.$route.params.id,
                }
            },
        },
    },
    computed: {
        venueCapacityUsage() {
            let capacityUsage = 0
            if (this.venue) {
                for (let activeEvent of this.venue.activeEvents) {
                    capacityUsage = capacityUsage + activeEvent.capacityInUse
                }
            }
            return capacityUsage
        },
    },
}
</script>

<style scoped lang="sass">
.event-overview
    .cards-container
        display: grid
        grid-template-columns: 1fr 1fr
        grid-gap: $m
        margin-bottom: $xl

        @include breakpoint('m')
            grid-template-columns: 1fr

        &.loader
            height: 158px
            background: $gray-1

        .card
            &.club
                flex: 3
                display: flex
                justify-content: space-between
                align-items: center
                background: $neutral-black

                .text-container
                    margin-bottom: $l

                    .label
                        color: $neutral-1
                        margin-bottom: $xxs

                    h1
                        color: white

                .club-image
                    border-radius: 11px
                    width: 100%
                    max-width: 110px
                    aspect-ratio: 1/1
                    background-size: cover

            &.stats
                flex: 1
                background: $yellow

                .stat + .stat
                    margin-top: $l

                .stat
                    display: flex
                    align-items: center

                    .number
                        font-size: 3.4rem
                        margin-right: $s
                        color: $neutral-black

                    .label
                        color: $neutral-black


    .events
        .delete-event-modal ::v-deep
            .modal-container
                top: 30%

            .modal-content
                max-width: 348px

        .events-header
            margin-bottom: $xl

            .title-container
                margin-bottom: $l

            .search-container
                border-bottom: 1px solid $gray-1

.tabs-container
    width: 100%
    padding: 12px 14px
    margin-bottom: $l
    box-sizing: border-box
    background-color: $neutral-white
    filter: drop-shadow(0px 2px 2px rgba(141, 141, 141, 0.11))
    border-radius: 12px
    display: flex

    .link + .link
        margin-left: $s

    .link
        display: flex
        align-items: center
        text-decoration: none
        padding: 0 $s
        border-radius: 5px
        +transition(100ms)
        color: $neutral-3
        height: $xl

        &.stroke
            .copy-m
                color: currentColor
                &::v-deep
                    svg
                        path
                            stroke: currentColor

            &:hover
                background: $neutral-1

            &.router-link-exact-active
                color: $primary-2

                span
                    color: currentColor

                &::v-deep
                    svg
                        path
                            stroke: currentColor

        &.fill
            .copy-m
                color: currentColor
                &::v-deep
                    svg
                        path
                            fill: currentColor

            &:hover
                background: $neutral-1

            &.router-link-exact-active
                color: $primary-2

                span
                    color: currentColor

                &::v-deep
                    svg
                        path
                            fill: currentColor

        .icon
            margin-right: $xs

.search-container
    height: 56px
    display: flex
    align-items: center

    .search
        display: flex
        flex: 1
        height: 100%
        align-items: center

        .feather-icon
            width: $m
            margin-right: $s

            &::v-deep
                svg
                    stroke: $neutral-2

        input
            background: none
            border: none
            height: 100%
            flex: 1
            outline: none
            @extend %copy-m
            +placeholder
                color: $neutral-2

            &:focus
                +placeholder
                    color: lighten($neutral-2, 10%)

    .filter-container
        cursor: pointer
        color: $neutral-2

        .filter
            display: flex
            align-items: center

            .text
                color: currentColor

            .feather-icon
                width: $m
                margin-left: $s

                &::v-deep
                    svg
                        stroke: $neutral-2




//
</style>
